import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Box, Typography, TextField, Button, Chip, CircularProgress, Tooltip, IconButton } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import PersonIcon from '@material-ui/icons/Person';
import PhoneIcon from '@material-ui/icons/Phone';
import EmailIcon from '@material-ui/icons/Email';
import EditIcon from '@material-ui/icons/Edit';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CreditCardIcon from '@material-ui/icons/CreditCard';
import { AuthContext } from '../../App';

const useStyles = makeStyles(() => ({
  root: {
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    padding: '10px 14px',
    marginTop: 10,
  },
  header: {
    display: 'flex', alignItems: 'center', gap: 8,
    marginBottom: 8,
  },
  headerIcon: {
    width: 26, height: 26, borderRadius: 8,
    background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  headerTitle: {
    fontSize: '0.68rem', fontWeight: 800, color: '#475569',
    textTransform: 'uppercase', letterSpacing: '0.05em',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 8,
  },
  field: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 8, fontSize: '0.78rem',
      background: '#f8fafc', height: 36,
    },
    '& .MuiOutlinedInput-input': { padding: '8px 10px' },
  },
  readonlyRow: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '6px 10px', fontSize: '0.74rem',
    background: '#f8fafc', borderRadius: 6,
    border: '1px solid #e2e8f0',
  },
  statusChip: {
    height: 20, fontSize: '0.58rem', fontWeight: 800,
  },
  actions: {
    display: 'flex', alignItems: 'center', gap: 6,
    marginTop: 8,
  },
}));

/**
 * Driver contact panel that appears in the vehicle assignment dialog.
 * Detects whether the selected vehicle's driver already has a local record.
 * If yes → read-only summary with "Editar" toggle. If not → editable form
 * for the operator to fill on first use.
 *
 * The persisted values are reported to the parent via `onChange(draftRecord)`
 * so that the assign-vehicle POST can include them in the same request.
 */
function DriverEditPanel({
  driverName,
  driverExternalId,
  driverTachoCard,
  lang = 'pt',
  onChange,
}) {
  const classes = useStyles();
  const { token } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [existing, setExisting] = useState(null); // { id, phone, email, notes, ... } or null
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ phone: '', email: '', notes: '' });

  // Lookup existing driver record whenever the selected vehicle driver changes
  const lookup = useCallback(async () => {
    if (!token || !driverName) {
      setExisting(null);
      setForm({ phone: '', email: '', notes: '' });
      setEditing(false);
      return;
    }
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (driverExternalId) qs.set('externalId', driverExternalId);
      if (driverTachoCard) qs.set('tachoCard', driverTachoCard);
      if (driverName) qs.set('name', driverName);
      const res = await fetch(`/planner-api/drivers/lookup?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setExisting(data.driver || null);
        if (data.driver) {
          setForm({
            phone: data.driver.phone || '',
            email: data.driver.email || '',
            notes: data.driver.notes || '',
          });
          setEditing(false);
        } else {
          setForm({ phone: '', email: '', notes: '' });
          setEditing(true); // new record → go straight to edit mode
        }
      }
    } catch {} finally {
      setLoading(false);
    }
  }, [token, driverName, driverExternalId, driverTachoCard]);

  useEffect(() => { lookup(); }, [lookup]);

  // Propagate the current draft upwards so the parent can attach it to the
  // assign-vehicle POST.
  useEffect(() => {
    if (!onChange) return;
    onChange({
      externalId: driverExternalId || null,
      tachoCard: driverTachoCard || null,
      name: driverName || null,
      phone: form.phone || null,
      email: form.email || null,
      notes: form.notes || null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.phone, form.email, form.notes, driverExternalId, driverTachoCard, driverName]);

  if (!driverName) return null;

  const hasTacho = !!driverTachoCard;

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box className={classes.headerIcon}>
          <PersonIcon style={{ fontSize: 14, color: '#fff' }} />
        </Box>
        <Typography className={classes.headerTitle}>
          {lang === 'pt' ? 'Motorista' : 'Driver'}
        </Typography>
        <Chip
          label={hasTacho ? (lang === 'pt' ? 'Tacógrafo' : 'Tacho') : (lang === 'pt' ? 'Sem cartão' : 'No card')}
          size="small"
          className={classes.statusChip}
          style={{
            background: hasTacho ? '#faf5ff' : '#f1f5f9',
            color: hasTacho ? '#7c3aed' : '#64748b',
            border: `1px solid ${hasTacho ? '#e9d5ff' : '#e2e8f0'}`,
          }} />
        {existing && (
          <Chip
            icon={<CheckCircleIcon style={{ fontSize: 12 }} />}
            label={lang === 'pt' ? 'Registado' : 'On record'}
            size="small"
            className={classes.statusChip}
            style={{ background: '#dcfce7', color: '#16a34a', border: '1px solid #86efac' }} />
        )}
        {!existing && !loading && (
          <Chip
            label={lang === 'pt' ? 'Novo' : 'New'}
            size="small"
            className={classes.statusChip}
            style={{ background: '#fef3c7', color: '#d97706', border: '1px solid #fde68a' }} />
        )}
        <Box style={{ flex: 1 }} />
        {existing && !editing && (
          <Tooltip title={lang === 'pt' ? 'Editar dados' : 'Edit details'}>
            <IconButton size="small" onClick={() => setEditing(true)} style={{ padding: 4 }}>
              <EditIcon style={{ fontSize: 14, color: '#64748b' }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Driver name (read-only, source of truth = EcooGPS) */}
      <Box style={{ fontSize: '0.82rem', fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>
        👤 {driverName}
        {driverTachoCard && (
          <Typography component="span" style={{ marginLeft: 8, fontSize: '0.58rem', color: '#94a3b8', fontFamily: 'monospace' }}>
            <CreditCardIcon style={{ fontSize: 10, verticalAlign: 'middle', marginRight: 2 }} />
            {driverTachoCard}
          </Typography>
        )}
      </Box>

      {loading ? (
        <Box style={{ display: 'flex', justifyContent: 'center', padding: 8 }}>
          <CircularProgress size={18} style={{ color: '#8b5cf6' }} />
        </Box>
      ) : (existing && !editing) ? (
        // Read-only summary
        <Box className={classes.grid}>
          <Box className={classes.readonlyRow}>
            <PhoneIcon style={{ fontSize: 14, color: '#22c55e' }} />
            <Typography style={{ fontSize: '0.74rem', fontWeight: 600, color: existing.phone ? '#0f172a' : '#cbd5e1' }}>
              {existing.phone || (lang === 'pt' ? 'Sem telefone' : 'No phone')}
            </Typography>
          </Box>
          <Box className={classes.readonlyRow}>
            <EmailIcon style={{ fontSize: 14, color: '#8b5cf6' }} />
            <Typography style={{ fontSize: '0.74rem', fontWeight: 600, color: existing.email ? '#0f172a' : '#cbd5e1', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {existing.email || (lang === 'pt' ? 'Sem email' : 'No email')}
            </Typography>
          </Box>
        </Box>
      ) : (
        // Editable form
        <Box className={classes.grid}>
          <TextField
            className={classes.field}
            variant="outlined"
            size="small"
            placeholder={lang === 'pt' ? 'Telefone (+351...)' : 'Phone (+351...)'}
            value={form.phone}
            onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}
            InputProps={{
              startAdornment: <PhoneIcon style={{ fontSize: 14, color: '#22c55e', marginRight: 6 }} />,
            }}
          />
          <TextField
            className={classes.field}
            variant="outlined"
            size="small"
            placeholder="email@exemplo.pt"
            value={form.email}
            onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
            InputProps={{
              startAdornment: <EmailIcon style={{ fontSize: 14, color: '#8b5cf6', marginRight: 6 }} />,
            }}
          />
        </Box>
      )}

      {editing && existing && (
        <Box className={classes.actions}>
          <Button size="small" onClick={() => { setEditing(false); setForm({ phone: existing.phone || '', email: existing.email || '', notes: existing.notes || '' }); }}
            style={{ fontSize: '0.65rem', textTransform: 'none', color: '#64748b' }}>
            {lang === 'pt' ? 'Cancelar' : 'Cancel'}
          </Button>
          <Box style={{ flex: 1 }} />
          <Typography style={{ fontSize: '0.58rem', color: '#94a3b8', fontStyle: 'italic' }}>
            {lang === 'pt' ? 'Os dados são gravados ao atribuir o veículo' : 'Saved when assigning the vehicle'}
          </Typography>
        </Box>
      )}

      {!existing && (
        <Typography style={{ fontSize: '0.58rem', color: '#94a3b8', marginTop: 6, fontStyle: 'italic' }}>
          {lang === 'pt'
            ? 'Primeira utilização — introduza os contactos. Serão gravados quando atribuir o veículo.'
            : 'First use — fill in contacts. They will be saved when assigning the vehicle.'}
        </Typography>
      )}
    </Box>
  );
}

export default DriverEditPanel;
